import { useTranslation } from "react-i18next";
import { NavLink } from "react-router-dom";
import { useAppSelector } from "@/app/hooks";
import { totalMonthlyRequests, totalSkus } from "@/features/shops/lib";
import { Package, Search } from "@/shared/ui/icons";
import { useGetCustomersCountQuery } from "./api";
import { selectSelectedPlan, selectShops } from "./legacySelectors";

function finiteNumber(value: unknown): number {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
}

interface PlanShape {
  title?: unknown;
  subscriptionTimestamp?: unknown;
  requestsLimit?: unknown;
  queries?: { threshold?: unknown };
}

export function UsageRecap() {
  const { t, i18n } = useTranslation();
  const shops = useAppSelector(selectShops);
  const selectedPlan = useAppSelector(selectSelectedPlan);
  const count = useGetCustomersCountQuery();
  const skuCount = count.isSuccess
    ? Object.values(count.data ?? {}).reduce<number>(
        (sum, value) => sum + finiteNumber(Number.parseInt(String(value), 10)),
        0,
      )
    : finiteNumber(totalSkus(shops));
  const monthlyQueries = finiteNumber(totalMonthlyRequests(shops));
  const [planKey = "trial", rawPlan = {}] = (Object.entries(selectedPlan)[0] || []) as [string?, PlanShape?];
  const plan: PlanShape =
    rawPlan?.title && typeof rawPlan.title === "object"
      ? { ...rawPlan, ...(rawPlan.title as PlanShape) }
      : rawPlan;
  const subscriptionTimestamp = finiteNumber(plan?.subscriptionTimestamp);
  // Stessa logica del legacy (App.jsx): il confronto con "adesso" deve restare live a ogni render,
  // altrimenti un abbonamento in scadenza durante la sessione non farebbe piu' uscire dallo stato di trial.
  // eslint-disable-next-line react-hooks/purity -- Date.now() e' voluto: vedi commento sopra
  const now = Date.now();
  const isTrial = planKey === "trial" || (subscriptionTimestamp > 0 && now < subscriptionTimestamp * 1000);
  const planThreshold =
    finiteNumber(plan?.requestsLimit) === 0
      ? finiteNumber(plan?.queries?.threshold)
      : finiteNumber(plan?.requestsLimit);
  const threshold = isTrial ? 1000 : planThreshold;
  const usagePercent = threshold > 0 ? Math.min(100, Math.max(0, (monthlyQueries / threshold) * 100)) : 0;
  // "always" e' il valore usato dal legacy; con lib ES2022 il tipo di useGrouping non lo conosce ancora (arriva con ES2023.Intl).
  const numberOptions = {
    maximumFractionDigits: 0,
    useGrouping: "always",
  } as unknown as Intl.NumberFormatOptions;
  const formatNumber = (value: number) =>
    new Intl.NumberFormat(i18n.language === "it" ? "it-IT" : "en-US", numberOptions).format(value);

  return (
    <section className="usage-recap" aria-label={t("shell.usage.title")}>
      <div className="usage-recap-header">
        <span>{t("shell.usage.title")}</span>
        <NavLink to="/billing/overview" aria-label={t("shell.usage.openBilling")}>
          {t("shell.usage.manage")}
        </NavLink>
      </div>
      <div className="usage-item" title={`${t("shell.usage.skus")}: ${formatNumber(skuCount)}`}>
        <span className="usage-icon">
          <Package aria-hidden="true" />
        </span>
        <span className="usage-copy">
          <span>{t("shell.usage.skus")}</span>
          <strong>{formatNumber(skuCount)}</strong>
        </span>
      </div>
      <div
        className="usage-item usage-monthly"
        title={`${t("shell.usage.monthlyQueries")}: ${formatNumber(monthlyQueries)}${threshold ? ` / ${formatNumber(threshold)}` : ""}`}
      >
        <span className="usage-icon">
          <Search aria-hidden="true" />
        </span>
        <span className="usage-copy">
          <span>{t("shell.usage.monthlyQueries")}</span>
          <strong>
            {formatNumber(monthlyQueries)}
            {threshold > 0 && <small> / {formatNumber(threshold)}</small>}
          </strong>
        </span>
      </div>
      {threshold > 0 ? (
        <div
          className="usage-progress"
          role="progressbar"
          aria-label={t("shell.usage.monthlyQueries")}
          aria-valuemin={0}
          aria-valuemax={threshold}
          aria-valuenow={Math.min(monthlyQueries, threshold)}
        >
          <span style={{ width: `${usagePercent}%` }} />
        </div>
      ) : (
        <span className="usage-unlimited">{t("shell.usage.unlimited")}</span>
      )}
      {isTrial && <span className="usage-caption">{t("shell.usage.trial")}</span>}
    </section>
  );
}
